'use client'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { captureAttribution, ctaTypeOf, pageTypeOf, track, withAttribution } from '@/lib/analytics'

/**
 * Mounted once in the root layout. Captures campaign parameters on landing,
 * tracks every conversion link click on the page (nav, hero, price cards, footer)
 * with one delegated listener, decorates outgoing signital.app links with the
 * stored attribution, and reports client-side navigations as `view_page`.
 */
export const Analytics = () => {
  const pathname = usePathname()
  const isFirstRender = useRef(true)

  useEffect(() => {
    captureAttribution()
  }, [])

  useEffect(() => {
    track('view_page', {
      page_path: pathname,
      page_type: pageTypeOf(pathname),
      navigation: isFirstRender.current ? 'initial' : 'client',
    })
    isFirstRender.current = false
  }, [pathname])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.href
      const ctaType = ctaTypeOf(href)
      if (!ctaType) return

      const decorated = withAttribution(href)
      if (decorated !== href) anchor.href = decorated

      const region =
        anchor.closest('[data-slice-type]')?.getAttribute('data-slice-type') ??
        (anchor.closest('nav') ? 'navigation' : anchor.closest('footer') ? 'footer' : 'page')

      track('cta_click', {
        cta_type: ctaType,
        cta_text: (anchor.textContent ?? '').trim().slice(0, 80),
        cta_location: region,
        link_url: decorated,
        page_path: window.location.pathname,
      })
    }
    // Capture phase so the event is seen before Next's Link handler or a target=_blank open.
    document.addEventListener('click', onClick, true)
    document.addEventListener('auxclick', onClick, true)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('auxclick', onClick, true)
    }
  }, [])

  return null
}

export default Analytics
